"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useReducedMotion } from "framer-motion";

const Scene = dynamic(() => import("./FooterHypercubeScene"), {
  ssr: false,
  loading: () => <StaticTesseract />,
});

const CYAN = "#2FD9C5";

/** Proiezione statica del tesseract (cubo-in-cubo): fallback per reduced-motion,
 *  placeholder prima del mount e su dispositivi senza WebGL. */
function StaticTesseract() {
  return (
    <svg viewBox="0 0 200 200" className="h-full w-full" aria-hidden="true">
      <g fill="none" stroke={CYAN} strokeWidth="1.5" strokeOpacity="0.75" strokeLinejoin="round">
        <rect x="24" y="24" width="152" height="152" rx="4" />
        <rect x="70" y="70" width="60" height="60" rx="3" strokeOpacity="0.5" />
        <line x1="24" y1="24" x2="70" y2="70" strokeOpacity="0.45" />
        <line x1="176" y1="24" x2="130" y2="70" strokeOpacity="0.45" />
        <line x1="24" y1="176" x2="70" y2="130" strokeOpacity="0.45" />
        <line x1="176" y1="176" x2="130" y2="130" strokeOpacity="0.45" />
      </g>
    </svg>
  );
}

/**
 * Modello 3D del footer: un tesseract (ipercubo 4D) = la "quarta dimensione",
 * il brand Dimensione 4. WebGL montato SOLO quando il footer entra a schermo;
 * frameloop in pausa quando esce (nessun consumo GPU). Reduced-motion -> statico.
 */
export function FooterHypercube() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting ?? false;
        setActive(visible);
        if (visible) setMounted(true);
      },
      { threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  return (
    <div
      ref={ref}
      className="relative aspect-square w-[clamp(13rem,20vw,17rem)]"
      aria-hidden="true"
    >
      <div className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative h-full w-full">
        {reduce ? (
          <div className="flex h-full w-full items-center justify-center p-6">
            <StaticTesseract />
          </div>
        ) : mounted ? (
          <Scene active={active} />
        ) : (
          <div className="flex h-full w-full items-center justify-center p-6">
            <StaticTesseract />
          </div>
        )}
      </div>
    </div>
  );
}
